import React, { useMemo, useState } from 'react';
import { Button, useDisclosure } from '@chakra-ui/react';
import { Link } from 'types/api.types';
import { DDL } from 'types/general.types';
import {
  PlaylistTypesListItem,
  ReleaseExport,
  ScReleaseExportValidations,
} from 'types/releases';
import { transformSchemaToYup } from 'app/utils/schemaToYup';
import * as yup from 'yup';
import { useGenres } from 'app/views/Release/hooks';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { AccountConnectionProviderId } from 'types/profile';
import { MultiTrackReleaseForm } from '../MultiTrackReleaseForm';
import {
  MultiTrackExportFormValues,
  MultiTrackExportRequestData,
} from '../types';
import {
  extendedDefaultValues,
  multiTrackFormErrorMessages,
} from '../constants';
import { formatMultiTrackExportData } from '../utils';

interface MultiTrackReleaseExportProps {
  releaseTitle: string;
  releaseExportLink?: Link;
  connectionGuid?: string;
  playlistTypes: PlaylistTypesListItem[];
  genresDDL: DDL;
  isDisabled: boolean;
  exportRelease: (data: MultiTrackExportRequestData) => Promise<ReleaseExport>;
  checkConnectionStatus: (
    providerId: AccountConnectionProviderId
  ) => Promise<string | undefined>;
  afterExport?: (
    providerId: AccountConnectionProviderId,
    exportGuid: string,
    connectionGuid?: string
  ) => void;
}

export const MultiTrackReleaseExport: React.FC<MultiTrackReleaseExportProps> = ({
  releaseTitle,
  releaseExportLink,
  connectionGuid,
  playlistTypes,
  genresDDL,
  isDisabled,
  exportRelease,
  checkConnectionStatus,
  afterExport,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [connectionId, setConnectionId] = useState<string | undefined>(
    connectionGuid
  );
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(false);
  const [openDialogDisabled, setOpenDialogDisabled] = useState(false);

  const {
    validationSchema,
    validationSchemaFetched,
  } = useFetchValidationSchema<ScReleaseExportValidations>({
    link: releaseExportLink,
  });

  const yupValidationSchema = useMemo(
    () =>
      validationSchemaFetched &&
      validationSchema &&
      transformSchemaToYup(
        validationSchema,
        {
          ...extendedDefaultValues,
        },
        multiTrackFormErrorMessages
      ),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validationSchema, validationSchemaFetched]
  );

  const concatenatedSchema = yup
    .object()
    .shape({
      releaseExportDate: yup.date().nullable().typeError('Invalid Date Format'),
    })
    .concat(yupValidationSchema || yup.object());

  const handleOpen = async () => {
    setOpenDialogDisabled(true);
    const renewedConnectionGuid = await checkConnectionStatus('SoundCloud');
    setConnectionId(renewedConnectionGuid);
    onOpen();
    setOpenDialogDisabled(false);
  };

  const onSubmit = async (values: MultiTrackExportFormValues) => {
    setFormSubmitDisabled(true);
    try {
      const renewedConnectionGuid = await checkConnectionStatus('SoundCloud');
      const data = await exportRelease(
        formatMultiTrackExportData(values, renewedConnectionGuid)
      );
      onClose();

      if (afterExport) {
        afterExport('SoundCloud', data.guid as string, renewedConnectionGuid);
      }
    } catch (e: any) {
      // Currently no handler needed
    } finally {
      setFormSubmitDisabled(false);
    }
  };

  const { genres } = useGenres({
    genresDDL,
    validationSchemaFetched,
    genresEnum: validationSchema?.properties?.playlist?.properties?.genre,
  });

  return (
    <>
      <Button
        size="sm"
        isDisabled={isDisabled || openDialogDisabled}
        onClick={handleOpen}
      >
        Export release
      </Button>

      {isOpen && (
        <MultiTrackReleaseForm
          releaseTitle={releaseTitle}
          playlistTypes={playlistTypes}
          onClose={onClose}
          onSubmit={onSubmit}
          yupValidationSchema={concatenatedSchema}
          genres={genres}
          isUserConnected={Boolean(connectionId)}
          disabled={formSubmitDisabled}
        />
      )}
    </>
  );
};
